import React, { useState } from 'react'
import { formatDate } from '@/v2/utils/dateHelpers'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import TablePagination from '@mui/material/TablePagination'
import Collapse from '@mui/material/Collapse'
import DownloadIcon from '@mui/icons-material/Download'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { currency } from '@/v2/utils/format'
import {
  DataTable,
  TableHeader,
  TableHeaderActions,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  EmptyState,
} from '../styles'

const RecentStartsSimple = ({ data, onExport }) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [expanded, setExpanded] = useState(true)
  const allData = data || []
  const startIndex = page * rowsPerPage
  const endIndex = startIndex + rowsPerPage
  const displayData = allData.slice(startIndex, endIndex)
  const totalRevenue = allData.reduce((sum, start) => {
    let amount = 0
    if (start.amount !== undefined && start.amount !== null) {
      if (typeof start.amount === 'string') {
        amount = parseFloat(start.amount) || 0
      } else if (typeof start.amount === 'number') {
        amount = start.amount
      }
    }
    return sum + amount
  }, 0)

  const formattedTotal = currency(totalRevenue)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <DataTable elevation={0}>
      <TableHeader
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme => theme.palette.primary?.dark || '#0056b3',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            sx={{
              color: 'white',
              p: 0.5,
            }}
            onClick={e => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            {expanded ? (
              <ExpandLessIcon style={{ color: 'white' }} />
            ) : (
              <ExpandMoreIcon style={{ color: 'white' }} />
            )}
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Treatment Starts
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}
            >
              All patients who started treatment in the selected period
            </Typography>
          </Box>
        </Box>
        <TableHeaderActions onClick={e => e.stopPropagation()}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 500,
              mr: 2,
            }}
          >
            {allData.length} starts • {formattedTotal} total
          </Typography>
          {onExport && (
            <Tooltip title="Export to CSV">
              <IconButton
                size="small"
                onClick={onExport}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#ffffff1a',
                  },
                }}
              >
                <DownloadIcon fontSize="small" style={{ color: 'white' }} />
              </IconButton>
            </Tooltip>
          )}
        </TableHeaderActions>
      </TableHeader>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {allData.length === 0 ? (
          <EmptyState>No starts data available for this period</EmptyState>
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeaderCell>
                      <Tooltip title="Date when the patient started treatment">
                        <span>Date</span>
                      </Tooltip>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip title="Patient name">
                        <span>Patient</span>
                      </Tooltip>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip title="Treatment plan amount">
                        <span>Amount</span>
                      </Tooltip>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip title="Marketing source (UTM source parameter)">
                        <span>Source</span>
                      </Tooltip>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip title="Marketing medium (UTM medium parameter)">
                        <span>Medium</span>
                      </Tooltip>
                    </TableHeaderCell>
                    <TableHeaderCell>
                      <Tooltip title="Marketing campaign (UTM campaign parameter)">
                        <span>Campaign</span>
                      </Tooltip>
                    </TableHeaderCell>
                  </tr>
                </TableHead>
                <tbody>
                  {displayData.map((start, index) => {
                    // Parse amount for display
                    let displayAmount = 0
                    if (start.amount !== undefined && start.amount !== null) {
                      if (typeof start.amount === 'string') {
                        displayAmount = parseFloat(start.amount) || 0
                      } else if (typeof start.amount === 'number') {
                        displayAmount = start.amount
                      }
                    }

                    return (
                      <TableRow
                        key={`${start.patient_name}-${start.date}-${index}`}
                      >
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {formatDate(start.date, 'MMM dd, yyyy')}
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {start.patient_name || '-'}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#05AD48',
                          }}
                        >
                          {currency(displayAmount)}
                        </td>
                        <td
                          style={{
                            padding: '16px 24px',
                            fontSize: '14px',
                            fontWeight: 600,
                          }}
                        >
                          {start.utm_source || 'Direct'}
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {start.utm_medium || '-'}
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {start.utm_campaign || '-'}
                        </td>
                      </TableRow>
                    )
                  })}
                </tbody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={allData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                borderTop: '1px solid #e0e0e0',
                '.MuiTablePagination-toolbar': {
                  paddingLeft: '24px',
                  paddingRight: '24px',
                },
              }}
            />
          </>
        )}
      </Collapse>
    </DataTable>
  )
}

export default RecentStartsSimple
